import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { DepartmentDto, DepartmentGetPaginatedDto } from "@/core/api/generated/v1";

export type DepartmentAutocompleteOption = BaseAutocompleteOption<DepartmentDto>;

export const getDepartmentTitle = (data: DepartmentDto): string =>
  StringHelper.join([data.name || data.localNumber], ", ", {
    skipEmpty: true,
  });

export const departmentToOption = (data: DepartmentDto): DepartmentAutocompleteOption => ({
  id: data.id!,
  title: getDepartmentTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DepartmentAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<DepartmentDto> {
  searchFilters?: Partial<Omit<DepartmentGetPaginatedDto, "search" | "includeIds">>;
}

export default function DepartmentAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DepartmentAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={departmentToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.departmentsApi.apiV1DepartmentsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          departmentGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          departmentGetPaginatedDto: {
            ...params.departmentGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [],
      }}
      label='Department'
      placeholder='Search for department...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='department' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option.data?.description}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
